/* eslint-disable @typescript-eslint/no-explicit-any */
import { createAction, createAsyncThunk } from '@reduxjs/toolkit'
import {
  createContact,
  deleteContactById,
  getAllContacts,
  updateContactById,
} from '@services/api/contact.api'
import { IContact } from '@dataTypes/contact.type'
import { INote } from '@dataTypes/note.type'
import { RootState } from '@store/index'

export const setContactNotes = createAction<{
  contactId: string
  notes: INote[]
}>('contact/setContactNotes')

export const fetchAllContacts = createAsyncThunk<
  IContact[],
  void,
  { rejectValue: string }
>('api/contact/fetch/all', async (_, { rejectWithValue }) => {
  try {
    const response = await getAllContacts()
    if (response.statusCode !== 200) {
      return rejectWithValue(response.message)
    }

    const rawContacts =
      (response.data &&
        'contacts' in response.data &&
        Array.isArray(response.data.contacts) &&
        response.data.contacts) ||
      (response.data &&
        'contact' in response.data &&
        Array.isArray(response.data.contact) &&
        response.data.contact) ||
      null

    if (rawContacts) {
      const contacts = (rawContacts as IContact[]).sort((a, b) =>
        a.date_time_created_at && b.date_time_created_at
          ? new Date(b.date_time_created_at).getTime() -
            new Date(a.date_time_created_at).getTime()
          : 0,
      )
      return contacts as IContact[]
    }

    // Handle the case where contacts are not in the response
    return rejectWithValue('Contacts not found in the response')
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})

export const addContact = createAsyncThunk<
  void,
  IContact,
  {
    rejectValue: string
  }
>(
  'api/contact/add',
  async (contactData, { dispatch, getState, rejectWithValue }) => {
    const state = getState() as RootState
    const clinicId = state.clinic.selectedClinic?.clinic_id as string
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await createContact({
        ...contactData,
        clinic_id: clinicId,
        created_by: user.user_id,
        updated_by: user.user_id,
      })

      if (response.statusCode !== 201) {
        return rejectWithValue(response.message || 'Failed to add contact')
      }

      dispatch(fetchAllContacts())
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to create contact')
    }
  },
)

export const updateContact = createAsyncThunk<
  IContact,
  { contact_id: string; updates: Partial<IContact> },
  { rejectValue: string }
>(
  'api/contact/update',
  async ({ contact_id, updates }, { rejectWithValue }) => {
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      const response = await updateContactById(contact_id, {
        ...updates,
        updated_by: user.user_id,
      })

      if (response.statusCode !== 200) {
        return rejectWithValue(response.message)
      }

      // Type narrowing for `response.data`
      if (
        response.data &&
        'contact' in response.data &&
        typeof response.data.contact === 'object' &&
        !Array.isArray(response.data.contact)
      ) {
        return response.data.contact as IContact // Ensure correct type
      }

      return rejectWithValue('Contact not found in the response')
    } catch (error: any) {
      return rejectWithValue(error.message || 'Error occurred during update')
    }
  },
)

export const deleteContact = createAsyncThunk<
  string,
  string,
  {
    rejectValue: string
  }
>('api/contact/delete', async (id, { rejectWithValue }) => {
  try {
    const response = await deleteContactById(id)
    if (response.statusCode !== 200) {
      throw new Error(response.message)
    }
    return id
  } catch (error: any) {
    return rejectWithValue(error.message || 'An error occurred')
  }
})
